import type { ActionTree } from 'vuex';
import type { RouteRecordRaw } from 'vue-router';
import type { UserState } from './typing';
import type { RootState } from '@/store/root-state';
import { RESET_CURRENT_USER, SET_INFO, SET_ROUTERS, SET_TOKEN } from './mutations';
import type { LoginParams, UserInfo } from '@/api/user/login';
import { postAccountLogin, getCurrentUser } from '@/api/user/login';
import { default as router, routes } from '@/router';
import { filterMenu } from '@/utils/menu-util';
import { generatorDynamicRouter } from '@/router/generator-routers';
import type { AxiosResponse } from 'axios';
import { loginRoutePath } from '@/router/define-meta';
import CryptoJS from 'crypto-js';
import {afterLogin, heartbeat} from "@/api/system/user";

export const DEFAULT_KEY = 'thsware666';
export const LOGIN = 'LOGIN';
export const LOGOUT = 'LOGOUT';
export const GET_INFO = 'GET_INFO';
export const GENERATE_ROUTES = 'GenerateRoutes';
export const GENERATE_ROUTES_DYNAMIC = '';

function parseJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export const actions: ActionTree<UserState, RootState> = {
  [LOGIN]({ commit }, info: LoginParams) {
    return new Promise((resolve, reject) => {
      // 采用des加密密码
      info.password = CryptoJS.DES.encrypt(info.password, CryptoJS.enc.Utf8.parse(DEFAULT_KEY), {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      }).toString();
      // call ajax
      postAccountLogin(info)
        .then((res: any) => {
          commit(SET_TOKEN, res.data.token);
          // 创建一条登录记录
          afterLogin().then((logRes) => {
            // 激活定时任务，每20s心跳一更次新登录状态
            if (logRes && logRes.data) {
              const intervalId = setInterval(() => {
                heartbeat(logRes.data.id).catch(() => {
                  clearInterval(intervalId);
                });
              }, 20000);
              const expTimestamp = parseJwt(res.data.token)?.exp;
              const now = Math.floor(Date.now()/1000);
              let expMill = 30 * 60 * 1000;
              if (expTimestamp && expTimestamp > now) {
                // JWT 的 exp 是以秒为单位的时间戳，需要乘以 1000 转换为毫秒
                expMill = (expTimestamp - now)*1000;
              }
              // token 过期后自动清除心跳
              setTimeout(() => {
                clearInterval(intervalId);
              }, expMill);
            }

            resolve(res);
          });
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  [GET_INFO]({ commit }) {
    return new Promise((resolve, reject) => {
      getCurrentUser()
        .then((res: AxiosResponse<UserInfo>) => {
          commit(SET_INFO, res.data);
          localStorage.setItem('userType', res.data?.userType);
          resolve(res.data);
        })
        .catch(err => {
          // 获取登录用户信息后，直接清理掉当前 token 并强制让流程走到登录页
          commit(SET_TOKEN, null);
          reject(err);
          // 如果你需要直接跳转登录页面
          location.replace(loginRoutePath);
        });
    });
  },
  // 从路由表构建路由（前端对比后端权限字段过滤静态路由表）
  [GENERATE_ROUTES]({ commit }) {
    return new Promise<RouteRecordRaw[]>(resolve => {
      // 修改这里可以进行接口返回的对象结构进行改变
      // 亦或是去掉 info.role 使用别的属性替代
      // 任何方案都可以，只需要将最后拼接构建好的路由数组使用
      // router.addRoute() 添加到当前运行时的路由中即可

      // const { permissions } = (info.role || {}) as Role;
      const allRoutes = filterMenu(routes);
      // const permissionsKey = permissions?.map(permission => permission.name);
      // const allowRoutes = !permissionsKey
      //   ? allRoutes
      //   : allRoutes.filter(route => {
      //       // parnent route filter
      //       const hasAllow = hasAuthority(route as MenuDataItem, permissionsKey!);
      //       if (hasAllow && route.children && route.children.length > 0) {
      //         // current route children filter
      //         route.children = filterChildRoute(route as MenuDataItem, permissionsKey!);
      //       }
      //       return hasAllow;
      //     });
      // console.log('allowRoutes', allowRoutes);
      // 添加到路由表
      const {
        // eslint-disable-next-line
        children: _,
        ...mainRoute
      } = routes[0];
      const route = {
        ...mainRoute,
        children: allRoutes,
      };
      router.addRoute(route);
      commit(SET_ROUTERS, allRoutes);
      resolve(allRoutes);
    });
  },
  // 从后端获取路由表结构体，并构建前端路由
  [GENERATE_ROUTES_DYNAMIC]({ commit }) {
    return new Promise<RouteRecordRaw>(resolve => {
      generatorDynamicRouter()
        .then((routes: RouteRecordRaw) => {
          const allowRoutes = routes.children || [];
          // 添加到路由表
          router.addRoute(routes);
          commit(SET_ROUTERS, allowRoutes);
          resolve(routes);
        })
        .catch(err => {
          console.error('generatorDynamicRouter', err);
        });
    });
  },
  [LOGOUT]({ commit }) {
    return new Promise<void>(resolve => {
      // todo 待后台提供登出，让token失效的方法
      // postLogout().finally(() => {
        commit(SET_TOKEN, null);
        commit(RESET_CURRENT_USER);
        localStorage.setItem('userType', null);
        resolve();
      // });
    });
  },
};
